import { deviceApi } from "#/api/deviceApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import ExportConfirmationModal from "#/components/overlays/ExportConfirmationModal";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import { useTableColumns } from "#/hooks/useTableColumns";
import { exportToCSV } from "#/utils/CsvUtils";
import { logger } from "#/utils/logger";
import { sanitize } from "#/utils/StringUtils";
import { getHardwareLabel } from "#/utils/deviceHardware";
import type { Device } from "#/types/device";
import { Button, Switch, Table, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDownload, IconEye, IconPlus, IconTrash } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatDateTimeForExport, formatTime } from "#/utils/dateTimeUtils";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DeleteDeviceModal from "./components/DeleteDeviceModal";
import DeviceRegistrationDrawer from "./components/DeviceRegistrationDrawer";
import DeviceStatusModal from "./components/DeviceStatusModal";

const DevicesPage = () => {
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite, canDelete } = usePermission();
  const { t } = useTranslation();
  const [
    createDrawerOpened,
    { open: openCreateDrawer, close: closeCreateDrawer },
  ] = useDisclosure(false);

  const [
    exportModalOpened,
    { open: openExportModal, close: closeExportModal },
  ] = useDisclosure(false);

  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [deviceToDelete, setDeviceToDelete] = useState<Device | undefined>(undefined);

  const [
    statusModalOpened,
    { open: openStatusModal, close: closeStatusModal },
  ] = useDisclosure(false);
  const [deviceToToggle, setDeviceToToggle] = useState<Device | undefined>(undefined);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [sortBy, setSortBy] = useState<string>("createdAt");

  // Columns come from the shared config filtered by the client's Column
  // Visibility setting; the Actions column is appended by the page.
  const { visible, headers: columnHeaders, sortableColumns, sortKeyAt } =
    useTableColumns<"devices", Device>("devices", {
      is_active: {
        sortKey: "is_active",
        cellClassName: "w-16",
        cell: (item) => (
          <PermissionTooltip disabled={!canWrite()}>
            <Switch
              checked={item.is_active === true}
              aria-label={
                item.is_active
                  ? t("devices.status.toggleInactive")
                  : t("devices.status.toggleActive")
              }
              disabled={!canWrite() || item.archived === true}
              onChange={() => {
                setDeviceToToggle(item);
                openStatusModal();
              }}
            />
          </PermissionTooltip>
        ),
      },
      name: {
        sortKey: "name",
        cellClassName: "min-w-[180px]",
        cell: (item) => item.name ?? "--",
      },
      mobility_type: {
        sortKey: "mobility_type",
        cellClassName: "capitalize min-w-[120px]",
        cell: (item) => sanitize(item.mobility_type?.toString() ?? "--"),
      },
      hardware_type: {
        sortKey: "hardware_type",
        cellClassName: "capitalize min-w-[120px]",
        cell: (item) => getHardwareLabel(item.hardware_type),
      },
      status_processing_type: {
        sortKey: "status_processing_type",
        cellClassName: "capitalize min-w-[160px]",
        cell: (item) => sanitize(item.status_processing_type?.toString() ?? "--"),
      },
      site: {
        // Kept as the page sent it before this refactor.
        cellClassName: "capitalize min-w-[150px]",
        cell: (item) => item.site?.name,
      },
      last_seen: {
        sortKey: "last_seen",
        cellClassName: "capitalize min-w-[180px]",
        cell: (item) =>
          item.last_seen ? (
            <span>
              {formatDate(item.last_seen, client)}
              <br />
              {formatTime(item.last_seen, client)}
            </span>
          ) : (
            "--"
          ),
      },
    });

  const prepareExportData = (devicesToExport: typeof items) => {
    return devicesToExport.map((item) => ({
      id: item.id || "",
      name: item.name || "",
      is_active: item.is_active ? "Yes" : "No",
      mobility_type: sanitize(item.mobility_type?.toString() || ""),
      hardware_type: getHardwareLabel(item.hardware_type, ""),
      status_processing_type: sanitize(item.status_processing_type?.toString() || ""),
      site: item.site?.name || "",
      last_seen: formatDateTimeForExport(item.last_seen),
    }));
  };

  const handleExportCurrentPage = () => {
    if (!items || items.length === 0) return;

    const exportData = prepareExportData(items);
    const exportHeaders = [
      "id",
      "name",
      "is_active",
      "mobility_type",
      "hardware_type",
      "status_processing_type",
      "site",
      "last_seen",
    ];

    const headerLabels = {
      id: "ID",
      name: "Name",
      is_active: "Active",
      mobility_type: "Mobility",
      hardware_type: "Hardware",
      status_processing_type: "Status Processing",
      site: "Site",
      last_seen: "Last Seen",
    };

    const note = t("common.utcNote");
    const filename = `devices-export-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
    exportToCSV(exportData, exportHeaders, filename, headerLabels, note);
  };

  const handleExportAll = async () => {
    try {
      const response = await deviceApi.getAll({
        limit: total,
        page: 1,
        ...(sortBy ? { sortBy: sortBy } : {}),
        ...(sortDirection ? { order: sortDirection } : {}),
      });

      const allDevices = response.data.data;
      const exportData = prepareExportData(allDevices);

      const exportHeaders = [
        "id",
        "name",
        "is_active",
        "mobility_type",
        "hardware_type",
        "status_processing_type",
        "site",
        "last_seen",
      ];

      const headerLabels = {
        id: "ID",
        name: "Name",
        is_active: "Active",
        mobility_type: "Mobility",
        hardware_type: "Hardware",
        status_processing_type: "Status Processing",
        site: "Site",
        last_seen: "Last Seen",
      };

      const note = t("common.utcNote");
      const filename = `devices-export-all-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      exportToCSV(exportData, exportHeaders, filename, headerLabels, note);
    } catch (error) {
      logger.error("Failed to export all devices:", error);
    }
  };

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getDevices,
  } = useQuery({
    queryKey: ["devices", sortBy, page, limit, sortDirection],
    queryFn: async () => {
      try {
        const response = await deviceApi.getAll({
          limit: limit,
          page: page,
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
        });
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch {
        throw new Error("No devices returned");
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    retry: false,
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex items-center justify-between bg-white z-10">
        <Title order={2}>{t("devices.title")}</Title>
        <div className="w-fit flex items-center justify-center gap-4">
          {/* <Popover
						anchor={
							<Button
								leftSection={<IconFilter size={18} />}
								variant={"outline"}
								color="black"
								size="sm"
								loading={isLoading}
							>
								Filters
							</Button>
						}
					>
						<div>sup</div>
					</Popover> */}
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={() => {
                openCreateDrawer();
              }}
              color="black"
              size="sm"
              disabled={!canWrite()}
            >
              {t("devices.registerScanner")}
            </Button>
          </PermissionTooltip>
          <Button
            leftSection={<IconDownload size={18} />}
            variant="outline"
            onClick={openExportModal}
            disabled={!items || items.length === 0}
            size="sm"
          >
            {t("common.exportCsv")}
          </Button>
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={[...columnHeaders, t("common.actions")]}
          loading={isPending}
          allowSorting={true}
          sortableColumns={sortableColumns}
          onSortChange={(e: TableSortProps) => {
            const key = sortKeyAt(e.index);
            if (!key) return;
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(key);
          }}
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                {visible.map((column) => (
                  <Table.Td key={column.key} className={column.cellClassName}>
                    {column.cell(item)}
                  </Table.Td>
                ))}
                <Table.Td style={{ minWidth: '140px' }}>
                  <div className="w-full flex items-center justify-center gap-2">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/devices/${item.id}`);
                      }}
                    />
                    <PermissionTooltip disabled={!canDelete()}>
                      <ActionButton
                        icon={IconTrash}
                        variant="outline"
                        color="red"
                        disabled={!canDelete()}
                        onClick={() => {
                          setDeviceToDelete(item);
                          openDeleteModal();
                        }}
                      />
                    </PermissionTooltip>
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <DeleteDeviceModal
        opened={deleteModalOpened}
        onClose={() => {
          closeDeleteModal();
          setDeviceToDelete(undefined);
        }}
        device={deviceToDelete}
        onSuccess={() => {
          void getDevices();
          setDeviceToDelete(undefined);
        }}
      />

      <DeviceStatusModal
        opened={statusModalOpened}
        onClose={() => {
          closeStatusModal();
          setDeviceToToggle(undefined);
        }}
        device={deviceToToggle}
        onSuccess={() => {
          void getDevices();
          closeStatusModal();
          setDeviceToToggle(undefined);
        }}
      />

      <DeviceRegistrationDrawer
        opened={createDrawerOpened}
        onClose={() => {
          closeCreateDrawer();
        }}
        onSuccess={() => {
          setPage(1);
          void getDevices();
          closeCreateDrawer();
        }}
      />

      <ExportConfirmationModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        currentPageCount={items.length}
        totalCount={total}
      />
    </div>
  );
};

export default DevicesPage;
